import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { AuditLog, AuditLogUserParams, AuditType, IAuditLogFilterParams } from "@app/audit-log/models/audit-log-type.model";
import { CallState, LoadingState } from "@app/root-store/api-result.state";
import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatestWith,
    filter,
    map,
    of,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
} from "rxjs";

import { FormBuilder } from "@angular/forms";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { LOG_CATEGORY } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { EntityService } from "@app/entity/services/entity.service";
import { ProjectService } from "@app/project/services/project.service";
import { TableActionDisplay } from "@app/shared/model/action.model";
import { ArrayFilterPipe } from "@app/shared/pipes/array-filter/array-filter.pipe";
import { TablePropertiesService } from "@app/shared/services/table-properties.service";
import { UserManagementService } from "@app/user/service/user-management.service";
import { DateTime } from "luxon";
import { LogFilterComponent } from "../log-filter/log-filter.component";

@Component({
    selector: "app-audit-log-list",
    templateUrl: "./audit-log-list.component.html",
    styleUrls: ["./audit-log-list.component.scss"],
    providers: [ArrayFilterPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogListComponent implements OnInit, OnDestroy {
    @ViewChild("logFilterComp") logFilterComp: LogFilterComponent;

    @Input() logCategory = "";
    @Input() columns: { [key: string]: string } = {};

    @Output() dataUpdated = new EventEmitter<AuditLog[]>();

    isFilterExpanded: boolean;
    isLogLoading = false;
    filterForm = this.fb.group({});
    logTypes: AuditType[] = [];
    initStartDate = DateTime.now().plus({ days: -7 }).startOf("day").toMillis();
    initEndDate = DateTime.now().endOf("day").toMillis();
    callState$ = new BehaviorSubject<CallState<any>>(LoadingState.init);
    updatedFilter = new BehaviorSubject<Record<string, any>>({});
    queryString$ = new BehaviorSubject<string>("");
    dataSource$: Observable<AuditLog[]>;
    unsubscribe$ = new Subject();
    userList: AuditLogUserParams[] = [];

    logFilterDisplay: Record<string, { key: string; label: string }> = {};

    public tableActions = (_selectedRow: AuditLog) => [];
    public tableActionOptions = TablePropertiesService.getActionOptions(this.tableActions, { display: TableActionDisplay.noaction });

    constructor(
        matIconRegistry: MatIconRegistry,
        domSanitizer: DomSanitizer,
        private readonly fb: FormBuilder,
        private readonly auditLogService: AuditLogService,
        private readonly projectService: ProjectService,
        private readonly cdr: ChangeDetectorRef,
        private readonly entityService: EntityService,
        private readonly userManagementService: UserManagementService,
        private readonly arrayFilterPipe: ArrayFilterPipe
    ) {
        matIconRegistry.addSvgIcon("cc-filter", domSanitizer.bypassSecurityTrustResourceUrl(`assets/images/filter.svg`));
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.initializeData();
        this.fetchLog();
        this.getLogParams()
            .pipe(take(1))
            .subscribe(params => {
                this.updatedFilter.next(params);
            });
    }

    filterChange(logFilter: Record<string, any>) {
        this.extractDisplayedFilters(logFilter);
        this.updateFilter(logFilter);
    }

    onBadgetFilterRemoved(key: string) {
        this.logFilterComp.resetFilterByField(key);
    }

    onQueryChange(query: string) {
        this.queryString$.next(query);
        this.extractDisplayedFilters({ query });
    }

    private updateFilter(logParams: Record<string, any>) {
        const { user = {}, startTime, endTime, subType, type } = logParams;
        this.updatedFilter.next({
            ...this.updatedFilter.value,
            email: user.email,
            startTime: startTime ? DateTime.fromMillis(startTime).toISO() : "",
            endTime: endTime ? DateTime.fromMillis(endTime).toISO() : "",
            subType,
            type,
        });
    }

    private extractDisplayedFilters(logFilter: Record<string, any>) {
        let logFilterDisplay = {
            ...this.logFilterDisplay,
        };

        Object.keys(logFilter).forEach(key => {
            logFilterDisplay = {
                ...logFilterDisplay,
                [key]: {
                    key,
                    label: logFilter[key],
                },
            };
        });

        if (logFilter.user) {
            logFilterDisplay = {
                ...logFilterDisplay,
                user: {
                    key: "user",
                    label: logFilter.user.userName,
                },
            };
        }

        // We don't want to show the date fields in the badget - Ashley - the UX decision maker
        if (logFilter.startTime) {
            delete logFilterDisplay.endTime;
        }

        if (logFilter.endTime) {
            delete logFilterDisplay.startTime;
        }

        if (!this.logCategory && logFilter.category) {
            logFilterDisplay = {
                ...logFilterDisplay,
                category: {
                    key: "category",
                    label: logFilter.category,
                },
            };
        } else {
            delete logFilterDisplay["category"];
        }

        this.logFilterDisplay = logFilterDisplay;
    }

    /**
     * Get the log.
     */
    private fetchLog() {
        this.dataSource$ = this.updatedFilter.pipe(
            tap(_ => {
                if (this.callState$.value === LoadingState.loaded) {
                    this.auditLogService.clearLogDataState();
                }
            }),
            takeUntil(this.unsubscribe$),
            switchMap(params =>
                this.auditLogService.getStoreAuditsState(params).pipe(
                    tap(auditState => {
                        this.callState$.next(auditState.callState);
                    }),
                    filter(auditState => auditState.callState === LoadingState.loaded),
                    map(auditState => Object.values(auditState.result || {})),
                    take(1)
                )
            ),
            combineLatestWith(this.queryString$),
            map(
                ([data, query]) =>
                    this.arrayFilterPipe.transform(data, query, "userName,createTime,endTime,email,auditTypeDisplay,value") as AuditLog[]
            ),
            shareReplay(1),
            tap(data => this.dataUpdated.emit(data))
        );
    }

    private getLogParams(): Observable<IAuditLogFilterParams> {
        const params: IAuditLogFilterParams = {};

        if (this.logCategory === LOG_CATEGORY.ACCOUNT) {
            return this.projectService.getActiveProject$().pipe(
                shareReplay(1),
                take(1),
                map(activeProj => ({
                    projectId: activeProj.id,
                }))
            );
        }
        return of(params);
    }

    /**
     * Initializing data.
     */
    private initializeData(): void {
        const initParams: any = {
            startTime: this.initStartDate,
            endTime: this.initEndDate,
        };

        if (this.logCategory) {
            initParams["category"] = this.logCategory;
            this.auditLogService
                .getAuditLogTypesByCategory(this.logCategory)
                .pipe(
                    take(1),
                    tap(logTypes => {
                        this.logTypes = logTypes;
                        this.cdr.markForCheck();
                    })
                )
                .subscribe();
        }

        this.getUserList();
        this.filterChange(initParams);
    }

    private getUserList() {
        this.entityService
            .getAuthUserEntity()
            .pipe(
                switchMap(entity => this.userManagementService.getEntityStaffList$(entity.id)),
                takeUntil(this.unsubscribe$),
                take(1)
            )
            .subscribe(users => {
                this.userList = users.map(user => ({
                    email: user.email,
                    userName: `${user.firstName} ${user.lastName}`,
                }));
            });
    }
}
